import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg, { getWebPageSchema, getBreadcrumbSchema, getArticleSchema } from '@/components/feature/SchemaOrg';
import Breadcrumb, { getBreadcrumbForArticle } from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章不存在</h2>
            <p className="text-sm text-foreground-500 mb-4">找不到该文章</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const pageTitle = `${article.title} - 开云体育`;
  const pageUrl = `https://www.kaiyunsports.com/article/${article.id}`;
  const breadcrumbItems = getBreadcrumbForArticle(article.id, article.category, article.categorySlug, article.title);
  const breadcrumbSchemaItems = breadcrumbItems.map((b) => ({
    name: b.label,
    url: b.href ? `https://www.kaiyunsports.com${b.href}` : undefined,
  }));

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={pageUrl}
        ogTitle={pageTitle}
        ogDescription={article.excerpt}
        ogType="article"
        ogUrl={pageUrl}
        ogImage={article.image}
      />
      <SchemaOrg {...getWebPageSchema(pageTitle, article.excerpt, pageUrl)} />
      <SchemaOrg {...getBreadcrumbSchema(breadcrumbSchemaItems)} />
      <SchemaOrg {...getArticleSchema(article.title, article.excerpt, pageUrl, article.date, article.author, article.image)} />

      <Navbar />

      <main className="pt-16">
        {/* Article Hero */}
        <section className="relative h-[260px] md:h-[360px]">
          <div className="absolute inset-0">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/20"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto h-full flex flex-col justify-end px-4 md:px-6 pb-8">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-background-50 mt-3 leading-tight">{article.title}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-background-50/80">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="px-2 py-0.5 bg-primary-500/80 text-background-50 rounded text-xs">{article.category}</span>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex gap-10">
              {/* Main Content */}
              <div className="flex-1 min-w-0">
                <div
                  className="prose-content text-sm leading-relaxed text-foreground-800"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags */}
                <div className="flex items-center gap-2 mt-8 pt-6 border-t border-background-200/60 flex-wrap">
                  <span className="text-xs text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">{tag}</span>
                  ))}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-200/60">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-arrow-left-line"></i> 返回{article.category}
                  </Link>
                  <Link
                    to="/"
                    className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                  >
                    返回首页 <i className="ri-home-line"></i>
                  </Link>
                </div>
              </div>

              {/* Sidebar - Desktop */}
              <aside className="hidden lg:block w-72 flex-shrink-0">
                <div className="sticky top-24">
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-4">相关推荐</h3>
                  <div className="flex flex-col gap-3">
                    {relatedArticles.length > 0 ? (
                      relatedArticles.map((rel) => (
                        <Link
                          key={rel.id}
                          to={`/article/${rel.id}`}
                          className="group flex gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/60 hover:border-primary-200 transition-all"
                        >
                          <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                            <img src={rel.image} alt={rel.title} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-xs font-medium text-foreground-800 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">{rel.title}</h4>
                            <p className="text-xs text-foreground-400 mt-1">{rel.date}</p>
                          </div>
                        </Link>
                      ))
                    ) : (
                      <p className="text-xs text-foreground-400">暂无相关推荐</p>
                    )}
                  </div>

                  {/* Quick Links */}
                  <div className="mt-6 p-4 rounded-lg bg-secondary-50 border border-secondary-100">
                    <h4 className="font-heading font-semibold text-foreground-900 text-xs mb-3">快速导航</h4>
                    <div className="flex flex-col gap-2">
                      <Link to="/category/platform-guide" className="text-xs text-foreground-600 hover:text-primary-600 transition-colors">
                        <i className="ri-guide-line mr-1.5"></i>平台指南
                      </Link>
                      <Link to="/category/sports-news" className="text-xs text-foreground-600 hover:text-primary-600 transition-colors">
                        <i className="ri-football-line mr-1.5"></i>赛事资讯
                      </Link>
                      <Link to="/category/promotions" className="text-xs text-foreground-600 hover:text-primary-600 transition-colors">
                        <i className="ri-gift-line mr-1.5"></i>活动福利
                      </Link>
                      <Link to="/about" className="text-xs text-foreground-600 hover:text-primary-600 transition-colors">
                        <i className="ri-information-line mr-1.5"></i>关于我们
                      </Link>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>

        {/* Related Articles - Mobile */}
        <section className="lg:hidden py-10 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h3 className="font-heading font-semibold text-foreground-900 mb-4">相关推荐文章</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group flex gap-3 p-3 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all"
                >
                  <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
                    <img src={rel.image} alt={rel.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground-800 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">{rel.title}</h4>
                    <p className="text-xs text-foreground-400 mt-1">{rel.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}